import { useCallback } from 'react';
import type { FunctionComponent } from 'react';

import Button from '../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import TextInput from '../../../generic/Forms/TextInput';
import Icon, { icons, IconText } from '../../../generic/Icon';
import Tooltip from '../../../generic/Tooltip';
import type { JiraComponent } from '../../../../../shared/jira';

type JiraProjectSettingsComponentsHeaderItemProps = {
  heading: string;
  tooltip?: string;
};

/**
 * Represents one of the "header" cells in the Components table.
 */
const JiraProjectSettingsComponentsHeaderItem: FunctionComponent<JiraProjectSettingsComponentsHeaderItemProps> = ({
  heading,
  tooltip,
}) => (
  <div className="jira-settings__header__item">
    {heading}
    {tooltip && [
      ' ',
      (
        <Tooltip id="components-header-item-tooltip" key="tooltip" tooltip={tooltip}>
          <span className="inline-block">
            <Icon icon={icons.infoCircle} />
          </span>
        </Tooltip>
      ),
    ]}
  </div>
);

type JiraProjectSettingsComponentsRowProps = JiraComponent & {
  isDisabled: boolean;
  onUpdateComponent: (component: Partial<JiraComponent>) => void;
  onDelete: () => void;
};

/**
 * Represents a single "row" in the custom components "table".
 */
const JiraProjectSettingsComponentsRow: FunctionComponent<JiraProjectSettingsComponentsRowProps> = ({
  onUpdateComponent,
  onDelete,
  isDisabled,
  ...props
}) => (
  <div className="jira-settings__components-table__row">
    <TextInput
      label="Component ID"
      placeholder="10000"
      size="small"
      value={props.id}
      onChange={id => onUpdateComponent({ id })}
      isDisabled={isDisabled}
    />
    <TextInput
      label="Component Display Name"
      placeholder="App"
      size="small"
      value={props.displayName}
      onChange={displayName => onUpdateComponent({ displayName })}
      isDisabled={isDisabled}
    />
    <div>
      <Button
        type={BUTTON_TYPE.OUTLINE}
        size={BUTTON_SIZE.MEDIUM}
        onClick={onDelete}
        icon={icons.trash}
        isDisabled={isDisabled}
      />
    </div>
  </div>
);

/**
 * Factory for creating a new custom field.
 */
const newComponent = (): JiraComponent => ({
  id: '',
  displayName: '',
});

type JiraComponentsProps = {
  components: JiraComponent[];
  isDisabled: boolean;
  onChange: (components: JiraComponent[]) => void;
};

/**
 * JiraComponents is a generic component for managing
 * jira components, used in both the integration settings page
 * and the project settings page.
 */
const JiraComponents: FunctionComponent<JiraComponentsProps> = ({
  components,
  isDisabled,
  onChange,
}) => {
  const onAddNewComponent = useCallback(() =>
    onChange([...components, newComponent()]), [onChange, components]);

  const getOnDeleteComponent = useCallback(
    (componentIndex: number) => () => onChange(components.filter((_component, index) => index !== componentIndex)),
    [onChange, components],
  );

  const getOnUpdateComponent = useCallback((componentIndex: number) => (updatedComponent: Partial<JiraComponent>) =>
    onChange(components.map((component, index) => index !== componentIndex
      ? component
      : {
          ...component,
          ...updatedComponent,
        })), [onChange, components]);

  return (
    <div className="jira-custom-Components">
      {components.length > 0 && (
        <header className="jira-settings__components-table__header">
          <JiraProjectSettingsComponentsHeaderItem heading="Component ID" />
          <JiraProjectSettingsComponentsHeaderItem
            heading="Component Display Name"
            tooltip="Name of the component when exporting an issue in FOSSA. Tip: Use the same name as in Jira to minimize confusion."
          />
          <JiraProjectSettingsComponentsHeaderItem heading="Delete" />
        </header>
      )}
      {components.map(({ id, displayName }, index) => (
        <JiraProjectSettingsComponentsRow
          id={id}
          displayName={displayName}
          isDisabled={isDisabled}
          onDelete={getOnDeleteComponent(index)}
          onUpdateComponent={getOnUpdateComponent(index)}
        />
      ))}
      <footer className="jira-setting__components-table__footer">
        <Button
          type={BUTTON_TYPE.SECONDARY}
          size={BUTTON_SIZE.MEDIUM}
          onClick={onAddNewComponent}
          isDisabled={isDisabled}
        >
          <IconText
            icon={icons.plus}
            text="Add Component"
          />
        </Button>
      </footer>
    </div>
  );
};

export default JiraComponents;
